import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Button,
  Col,
  Icon,
  Layout,
  Loading,
  MultiColumnList,
  MultiSelection,
  Pane,
  Row,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useStripes } from '@folio/stripes/core';
import useCubeMetaAPI from '../../hooks/useCubeMetaAPI';
import useFieldLabelsAPI from '../../hooks/useFieldLabelsAPI';
import ErrorMessage from '../../components/common/errorMessage/ErrorMessage';
import FieldLabelEditModal from './FieldLabelEditModal';

/**
 * FieldLabelsList — shared component for all three translation management pages.
 *
 * Props:
 *  fieldType      — 'table' | 'dimension' | 'measure'
 *  paneTitleId    — intl key for the Pane title
 *  nameColumnId   — intl key for the name column header
 *  paneId         — HTML id for the Pane
 *  listId         — HTML id for the MultiColumnList
 *  showCubeSelect — whether to show the cube/table selector (dimensions & measures)
 *  getItems       — fn(response, selectedCube) → array of { name, title, shortTitle }
 *  getItemName    — fn(item) → display name (short part after the dot)
 */
function FieldLabelsList({
  fieldType,
  paneTitleId,
  nameColumnId,
  paneId,
  listId,
  showCubeSelect,
  getItems,
  getItemName,
}) {
  const intl = useIntl();

  const location = useLocation();
  const history  = useHistory();

  // Read selectedCubes from URL on mount and refresh
  const getSelectedFromURL = () => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('cubes');
    if (!raw) return [];
    try {
      // URL stores only values (strings), rebuild {label,value} after meta loads
      const values = JSON.parse(raw);
      return Array.isArray(values) ? values : [];
    } catch {
      return [];
    }
  };

  const [selectedCubeValues, setSelectedCubeValues] = useState(getSelectedFromURL);
  const [editItem, setEditItem] = useState(null);
  const stripes = useStripes();
  const canManage = stripes.hasPerm('ui-reports.field-labels.manage');

  const { isLoading: metaLoading, error: metaError, response } = useCubeMetaAPI(
    showCubeSelect ? { type: 'table' } : undefined
  );
  const { labelsMap, isLoading: labelsLoading } = useFieldLabelsAPI({ fieldType });
  const { labelsMap: tableLabels } = useFieldLabelsAPI({ fieldType: 'table' });

  const isLoading = metaLoading || labelsLoading;

  // For dimensions/measures: fetch fields for selected cubes from server
  const selectedCubesParam = selectedCubeValues.join(',');
  const { response: fieldsResponse, isLoading: fieldsLoading } = useCubeMetaAPI(
    showCubeSelect && selectedCubeValues.length > 0
      ? { type: fieldType, cubes: selectedCubesParam }
      : undefined
  );

  // Rebuild {label, value} objects from stored values + meta
  const selectedCubes = useMemo(() => {
    if (!response?.cubes) return [];
    return selectedCubeValues
      .map(val => {
        const cube = response.cubes.find(c => c.name === val);
        if (!cube) return null;
        return { value: cube.name, label: tableLabels[cube.name] || cube.title || cube.name };
      })
      .filter(Boolean);
  }, [selectedCubeValues, response, tableLabels]);

  // Sync to URL — store values only
  const handleCubesChange = (newCubes) => {
    const values = newCubes.map(c => c.value);
    setSelectedCubeValues(values);
    const params = new URLSearchParams(location.search);
    if (values.length > 0) {
      params.set('cubes', JSON.stringify(values));
    } else {
      params.delete('cubes');
    }
    history.replace({ search: params.toString() });
  };

  // Build rows from selected cubes (multi-select) or all cubes (tables page)
  const rows = useMemo(() => {
    if (!showCubeSelect) {
      // Tables page — show all cubes from response
      return (response?.cubes || []).map(cube => ({
        id:           cube.name,
        fieldKey:     cube.name,
        displayName:  cube.name,
        defaultTitle: cube.title || cube.name,
        currentLabel: labelsMap[cube.name] || cube.title || cube.name,
      }));
    }
    // Dimensions/Measures — use server-filtered fieldsResponse
    const allItems = [];
    (fieldsResponse?.cubes || []).forEach(cube => {
      const cubeItems = getItems(fieldsResponse, cube.name);
      cubeItems.forEach(item => {
        allItems.push({
          id:           item.name,
          fieldKey:     item.name,
          displayName:  getItemName(item),
          defaultTitle: item.title || item.name,
          currentLabel: labelsMap[item.name] || item.shortTitle || item.title || item.name,
          cubeName:     cube.name,
        });
      });
    });
    return allItems;
  }, [showCubeSelect, response, fieldsResponse, labelsMap, getItems, getItemName]);

  const showList = showCubeSelect ? selectedCubeValues.length > 0 : true;
  const listLoading = isLoading || (showCubeSelect && selectedCubeValues.length > 0 && fieldsLoading);

  return (
    <Pane
      fluidContentWidth
      id={paneId}
      paneTitle={<FormattedMessage id={paneTitleId} />}
      paneSub={
        !isLoading && !metaError ? (
          <FormattedMessage
            id="stripes-smart-components.searchResultsCountHeader"
            values={{ count: rows.length }}
          />
        ) : null
      }
    >
      {(isLoading || listLoading) && (
        <Layout className="centered full" style={{ maxWidth: '15rem', height: '8rem' }}>
          &nbsp;<Loading size="xlarge" />
        </Layout>
      )}

      {metaError && <ErrorMessage errorMessage={metaError.toString()} />}

      {!isLoading && !metaError && (
        <>
          {showCubeSelect && (
            <Row>
              <Col xs={6}>
                <MultiSelection
                  id="field-labels-cube-multiselect"
                  label={
                    <FormattedMessage
                      id="ui-reports.queryBuilder.tablesField.label"
                      defaultMessage="Table"
                    />
                  }
                  placeholder={intl.formatMessage({
                    id: 'ui-reports.dataModels.availableTables.selectTable.label',
                    defaultMessage: 'Select table',
                  })}
                  dataOptions={(response?.cubes || []).map(c => ({
                    label: tableLabels[c.name] || c.title || c.name,
                    value: c.name,
                  }))}
                  value={selectedCubes}
                  onChange={handleCubesChange}
                />
              </Col>
            </Row>
          )}

          {showList && (
            <MultiColumnList
              id={listId}
              interactive={false}
              visibleColumns={['rowIndex', 'displayName', 'currentLabel', 'actions']}
              contentData={rows}
              columnMapping={{
                rowIndex:     '#',
                displayName:  intl.formatMessage({ id: nameColumnId }),
                currentLabel: intl.formatMessage({
                  id: 'ui-reports.fieldLabels.currentLabel',
                  defaultMessage: 'Current Translation',
                }),
                actions: '',
              }}
              columnWidths={{ rowIndex: '5%', displayName: '35%', currentLabel: '45%', actions: '15%' }}
              formatter={{
                rowIndex:     item => item.rowIndex + 1,
                displayName:  item => item.displayName,
                currentLabel: item => item.currentLabel,
                actions: item => canManage ? (
                  <Button
                    buttonStyle="slim"
                    onClick={() => setEditItem({ fieldKey: item.fieldKey, displayName: item.defaultTitle })}
                  >
                    <Icon icon="edit" />
                  </Button>
                ) : null,
              }}
              autosize
              virtualize
            />
          )}
        </>
      )}

      {editItem && (
        <FieldLabelEditModal
          fieldKey={editItem.fieldKey}
          displayName={editItem.displayName}
          onClose={() => setEditItem(null)}
        />
      )}
    </Pane>
  );
}

FieldLabelsList.propTypes = {
  fieldType:      PropTypes.oneOf(['table', 'dimension', 'measure']).isRequired,
  paneTitleId:    PropTypes.string.isRequired,
  nameColumnId:   PropTypes.string.isRequired,
  paneId:         PropTypes.string.isRequired,
  listId:         PropTypes.string.isRequired,
  showCubeSelect: PropTypes.bool,
  getItems:       PropTypes.func,
  getItemName:    PropTypes.func,
};

FieldLabelsList.defaultProps = {
  showCubeSelect: false,
  getItems:       () => [],
  getItemName:    item => (item.name.includes('.') ? item.name.split('.').pop() : item.name),
};

export default FieldLabelsList;
